<template>
  <!-- cells are built once as plain dom in mounted(); the conductor then
       writes glyph text nodes and animation names directly, so a tick
       costs zero vue re-renders no matter how many cells are mid-flip -->
  <span ref="root" class="sf" :class="{ 'sf--static': reduced }" aria-hidden="true"></span>
</template>

<script>
// a row of split-flap character cells. each cell mechanically cycles
// through the drum (one direction, with wraparound, like the real
// hardware) until it lands on its target character. four layers per
// mid-flip cell: static top = incoming char, static bottom = outgoing
// char, a falling flap (outgoing top half) and a rising flap (incoming
// bottom half), replayed per step by alternating between two identical
// keyframe names.
const DRUM = ' ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789:./-,&\''
const TICK_MS = 120
// strides adapt to distance so every spin lands within about six flips:
// long spins sample the drum coarsely, short ones flip char by char
const MIN_STRIDE = 3
const MAX_STRIDE = 8

// one shared conductor drives every flap instance on the page, and it
// rides requestAnimationFrame so a tick's full cost (state walk + direct
// dom writes) lands inside one frame callback: one style/layout/paint
// pass per tick instead of a timer firing mid-frame and straddling two.
// raf also idles in hidden tabs, so a backgrounded wall board costs
// nothing and flutters the last stretch into place when it comes back.
const instances = new Set()
let rafId = null
let lastTick = 0
function conductorFrame(ts) {
  rafId = requestAnimationFrame(conductorFrame)
  if (ts - lastTick < TICK_MS - 6) return
  lastTick = ts
  instances.forEach(component => component.step())
}
function ensureConductor() {
  if (rafId !== null) return
  lastTick = 0
  rafId = requestAnimationFrame(conductorFrame)
}
function releaseConductor() {
  if (rafId !== null && instances.size === 0) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
}

// rolling-wave counter: instances spin up in registration-order waves so
// only a moving band of cells is ever mid-flip at once
let waveSeq = 0

export default {
  name: 'split_flap',
  props: {
    value: { type: String, default: '' },
    length: { type: Number, required: true },
  },
  data() {
    const reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    return {
      reduced: !!reduced,
    }
  },
  watch: {
    value() {
      this.applyValue()
    },
    length() {
      this.buildCells()
      this.applyValue()
    },
  },
  created() {
    // deliberately non-reactive: the conductor mutates cells and their
    // cached dom nodes every tick, and routing hundreds of mid-flip cells
    // through vue's reactivity + vdom per tick was the full-board stutter
    this._cells = []
  },
  mounted() {
    this.buildCells()
    this.applyValue()
  },
  beforeDestroy() {
    instances.delete(this)
    releaseConductor()
  },
  methods: {
    // build the static cell skeletons once and cache the nodes the
    // conductor writes per tick: four glyph text nodes, two flap elements
    // and the cell root. the four layers are always present - flaps only
    // paint while .sfc--live is on - so no per-tick node churn.
    buildCells() {
      const root = this.$refs.root
      if (!root) return
      root.textContent = ''
      const cells = []
      for (let i = 0; i < this.length; i++) {
        const el = document.createElement('span')
        el.className = 'sfc'
        const layer = cls => {
          const wrap = document.createElement('span')
          wrap.className = cls
          const glyph = document.createElement('span')
          glyph.className = 'sfc-glyph'
          const text = document.createTextNode(' ')
          glyph.appendChild(text)
          wrap.appendChild(glyph)
          el.appendChild(wrap)
          return { wrap, text }
        }
        const top = layer('sfc-half sfc-half--top')
        const bottom = layer('sfc-half sfc-half--bottom')
        const flapTop = layer('sfc-flap sfc-flap--top')
        const flapBottom = layer('sfc-flap sfc-flap--bottom')
        root.appendChild(el)
        cells.push({
          el,
          topText: top.text,
          bottomText: bottom.text,
          flapTopEl: flapTop.wrap,
          flapTopText: flapTop.text,
          flapBottomEl: flapBottom.wrap,
          flapBottomText: flapBottom.text,
          // drum indices instead of chars keep the hot loop arithmetic
          curIdx: 0,
          nextIdx: 0,
          targetIdx: 0,
          hold: 0,
          flipId: 0,
          live: false,
        })
      }
      this._cells = cells
    },
    // uppercase, pad/truncate to length, and coerce anything outside the
    // drum to a blank flap; returns drum indices
    normalize(value) {
      const raw = String(value || '').toUpperCase().slice(0, this.length)
      const idxs = []
      for (let i = 0; i < this.length; i++) {
        const pos = DRUM.indexOf(raw[i] || ' ')
        idxs.push(pos === -1 ? 0 : pos)
      }
      return idxs
    },
    applyValue() {
      if (!this._cells.length) return
      const target = this.normalize(this.value)
      if (this._waveSeq === undefined) this._waveSeq = waveSeq++
      const baseHold = (this._waveSeq % 12) * 2
      let dirty = false
      this._cells.forEach((cell, i) => {
        cell.targetIdx = target[i]
        if (cell.curIdx === cell.targetIdx) return
        if (this.reduced) {
          cell.curIdx = cell.nextIdx = cell.targetIdx
          const ch = DRUM[cell.targetIdx]
          cell.topText.data = ch
          cell.bottomText.data = ch
          return
        }
        // wave offset + left-to-right stagger + jitter: the cascade reads
        // mechanical, and only a rolling band of cells is mid-flip at once
        cell.hold = baseHold + Math.round(i * 0.5 + Math.random() * 2)
        dirty = true
      })
      if (dirty) {
        instances.add(this)
        ensureConductor()
      }
    },
    // walk forward up to the adaptive stride without overshooting
    drumStep(cell) {
      const len = DRUM.length
      const distance = (cell.targetIdx - cell.curIdx + len) % len
      const stride = Math.min(MAX_STRIDE, Math.max(MIN_STRIDE, Math.ceil(distance / 6)))
      cell.nextIdx = (cell.curIdx + Math.min(stride, distance)) % len
    },
    step() {
      let busy = false
      this._cells.forEach(cell => {
        // commit the flap that finished falling on the previous tick
        if (cell.curIdx !== cell.nextIdx) {
          cell.curIdx = cell.nextIdx
          const ch = DRUM[cell.curIdx]
          cell.bottomText.data = ch
          cell.flapTopText.data = ch
        }
        if (cell.curIdx === cell.targetIdx) {
          if (cell.live) {
            cell.live = false
            cell.el.classList.remove('sfc--live')
          }
          return
        }
        busy = true
        if (cell.hold > 0) {
          cell.hold--
          return
        }
        this.drumStep(cell)
        cell.flipId++
        const suffix = cell.flipId % 2 ? 'b' : 'a'
        const ch = DRUM[cell.nextIdx]
        cell.topText.data = ch
        cell.flapBottomText.data = ch
        // swapping between the identical a/b keyframe names replays the
        // flap animation for this step
        cell.flapTopEl.style.animationName = 'sfc-fall-' + suffix
        cell.flapBottomEl.style.animationName = 'sfc-rise-' + suffix
        if (!cell.live) {
          cell.live = true
          cell.el.classList.add('sfc--live')
        }
      })
      if (!busy) {
        instances.delete(this)
        releaseConductor()
      }
    },
  },
}
</script>

<style>
/* unscoped on purpose: the cells are built imperatively, so they never
   receive the parent's scope attribute. all selectors are sf-prefixed,
   so nothing can leak. cell metrics (0.66em wide, 0.06em gap) are
   mirrored by the sf-col-* width helpers in departure_board.vue and the
   vb-sf-col-* helpers in vsm_board.vue; keep them in sync */
.sf {
  display: inline-flex;
  gap: 0.06em;
  line-height: 1;
}
.sfc {
  position: relative;
  display: inline-block;
  width: 0.66em;
  height: 1.18em;
  /* the cell owns its box completely (explicit size, absolute layers,
     internal divider), so containment keeps each tick's text writes and
     class toggles from invalidating layout or paint beyond the cell */
  contain: strict;
}
/* idle cells stay completely flat: with ~800 cells on a board, keeping
   flaps mounted (let alone 3d-transformed) on every cell forces a
   compositor layer explosion that froze painting outright. flaps render
   only while a cell is mid-flip, and they animate with a scaleY squash
   rather than rotateX so no perspective layers exist at all. */
.sfc::after {
  content: '';
  position: absolute;
  left: 0; right: 0;
  top: 50%;
  height: 1px;
  margin-top: -0.5px;
  background: rgba(0, 0, 0, 0.55);
  z-index: 3;
}
.sfc-half,
.sfc-flap {
  position: absolute;
  left: 0; right: 0;
  height: 50%;
  overflow: hidden;
  background: linear-gradient(180deg, #2a2a30 0%, #232329 100%);
}
.sfc-half--top,
.sfc-flap--top { top: 0; border-radius: 0.09em 0.09em 0 0; }
.sfc-half--bottom,
.sfc-flap--bottom {
  bottom: 0;
  border-radius: 0 0 0.09em 0.09em;
  background: linear-gradient(180deg, #1e1e23 0%, #18181c 100%);
}
.sfc-glyph {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 1.18em;
  line-height: 1.18em;
  text-align: center;
  font-weight: 600;
  color: currentColor;
}
.sfc-half--bottom .sfc-glyph,
.sfc-flap--bottom .sfc-glyph { top: auto; bottom: 0; }

.sfc-flap { display: none; }
.sfc--live .sfc-flap { display: block; }
/* fall + delayed rise total 110ms, inside the 120ms tick, so each flap
   cycle completes instead of being cancelled mid-flight every tick */
.sfc-flap--top {
  z-index: 2;
  transform-origin: 50% 100%;
  animation: sfc-fall-a 0.055s ease-in forwards;
}
.sfc-flap--bottom {
  z-index: 2;
  transform-origin: 50% 0%;
  transform: scaleY(0);
  animation: sfc-rise-a 0.055s ease-out 0.055s forwards;
}
/* a/b pairs are identical; swapping the name is what replays them */
@keyframes sfc-fall-a {
  from { transform: scaleY(1); }
  to { transform: scaleY(0); }
}
@keyframes sfc-fall-b {
  from { transform: scaleY(1); }
  to { transform: scaleY(0); }
}
@keyframes sfc-rise-a {
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
}
@keyframes sfc-rise-b {
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
}
.sf--static .sfc-flap { display: none; }
</style>
